import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const STORAGE_KEY = 'dashboard_last_stats';
const DASHBOARD_AUTH_KEY = 'dashboard_authed';
const DASHBOARD_PASSWORD = 'AA';

const PasswordGate = ({ onUnlock }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === DASHBOARD_PASSWORD) {
      sessionStorage.setItem(DASHBOARD_AUTH_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setInput('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">🔒</div>
          <h1 className="text-xl font-bold text-gray-800">Dashboard Access</h1>
          <p className="text-gray-400 text-sm mt-1">Enter password to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(false); }}
            autoFocus
            placeholder="Password"
            className={`w-full border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              error ? 'border-red-400' : 'border-gray-300'
            }`}
          />
          {error && <p className="text-red-500 text-sm">Incorrect password</p>}
          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-xl transition"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, accent, delta }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-start gap-4`}>
    <div className={`text-3xl p-3 rounded-xl ${accent}`}>{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-3xl font-bold text-gray-800 mt-0.5">{value ?? '—'}</p>
      {delta !== null && delta !== undefined && (
        <p className={`text-xs mt-1 font-medium ${delta > 0 ? 'text-green-500' : delta < 0 ? 'text-red-400' : 'text-gray-400'}`}>
          {delta > 0 ? `+${delta} since last visit` : delta < 0 ? `${delta} since last visit` : 'No change since last visit'}
        </p>
      )}
    </div>
  </div>
);

const FeedbackList = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(DASHBOARD_AUTH_KEY) === '1');
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [lastStats, setLastStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!authed) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) setLastStats(JSON.parse(saved));

    const fetchAll = async () => {
      try {
        const [feedbackRes, statsRes] = await Promise.all([
          axiosInstance.get('/api/feedback'),
          axiosInstance.get('/api/feedback/otherInformation'),
        ]);
        setFeedbacks(feedbackRes.data);
        setStats(statsRes.data);
        // Save current stats for next visit delta calculation
        localStorage.setItem(STORAGE_KEY, JSON.stringify(statsRes.data));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [authed]);

  if (!authed) return <PasswordGate onUnlock={() => setAuthed(true)} />;

  const delta = (key) => {
    if (!lastStats || !stats) return null;
    return (stats[key] || 0) - (lastStats[key] || 0);
  };

  const handleSendEmail = async () => {
    if (!window.confirm('Send email to ALL users?')) return;
    setSendingEmail(true);
    try {
      await axiosInstance.post('/api/feedback/sendEmail');
      alert('Email sent successfully to all users');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.totalUsers, icon: '👥', accent: 'bg-indigo-50 text-indigo-500', key: 'totalUsers' },
    { label: 'Workers', value: stats?.totalWorkers, icon: '👷', accent: 'bg-blue-50 text-blue-500', key: 'totalWorkers' },
    { label: 'Employers', value: stats?.totalEmployers, icon: '🧑‍💼', accent: 'bg-emerald-50 text-emerald-500', key: 'totalEmployers' },
    { label: 'Both Roles', value: stats?.totalBoth, icon: '🔄', accent: 'bg-purple-50 text-purple-500', key: 'totalBoth' },
    { label: 'Page Visits', value: stats?.totalVisitors, icon: '👁️', accent: 'bg-yellow-50 text-yellow-500', key: 'totalVisitors' },
    { label: '"Ask for Job" Clicks', value: stats?.askForJobCount, icon: '🔍', accent: 'bg-orange-50 text-orange-500', key: 'askForJobCount' },
    { label: '"Post a Job" Clicks', value: stats?.postAJobCount, icon: '📋', accent: 'bg-pink-50 text-pink-500', key: 'postAJobCount' },
    { label: 'Password Resets', value: stats?.totalResetPasswords, icon: '🔒', accent: 'bg-red-50 text-red-400', key: 'totalResetPasswords' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-400 text-sm mt-1">Last refreshed: {new Date().toLocaleTimeString()}</p>
        </div>
        <button
          onClick={handleSendEmail}
          disabled={sendingEmail}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl transition disabled:opacity-60 text-sm"
        >
          {sendingEmail ? '⏳ Sending...' : '📧 Email All Users'}
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {statCards.map((s) => (
          <StatCard key={s.key} label={s.label} value={s.value} icon={s.icon} accent={s.accent} delta={delta(s.key)} />
        ))}
      </div>

      {/* Recent sign-ups */}
      {stats?.recentUsers?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-10">
          <h2 className="text-lg font-bold text-gray-800 mb-4">🆕 Recent Sign-ups</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs uppercase tracking-wide border-b border-gray-100">
                  <th className="pb-2 pr-4">Name</th>
                  <th className="pb-2 pr-4">Role</th>
                  <th className="pb-2 pr-4">Location</th>
                  <th className="pb-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentUsers.map((u, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium text-gray-800">{u.name}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        u.role === 'employer' ? 'bg-emerald-100 text-emerald-700'
                        : u.role === 'worker' ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                      }`}>{u.role}</span>
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{u.location}</td>
                    <td className="py-2 text-gray-400 text-xs">{u.createdAt ? new Date(u.createdAt).toLocaleString('en-AU', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Feedback */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">💬 Feedback ({feedbacks.length})</h2>
        {feedbacks.length === 0 ? (
          <p className="text-gray-400 text-sm">No feedback yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {feedbacks.map((feedback) => (
              <div key={feedback._id} className="border border-gray-100 rounded-xl p-4 hover:shadow-md transition">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-800">{feedback.name}</h3>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    feedback.status === 'new' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                  }`}>{feedback.status}</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">{feedback.email}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{feedback.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackList;
